import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomerChatButton from '../components/CustomerChatButton';
import SupportChatButton from '../components/SupportChatButton';
import MessageConsole from '../components/MessageConsole';
// CalendarSyncModal removed (Jan 15, 2026) - moved to ProfileSettings
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, CalendarDays, ChevronDown } from 'lucide-react';
import useMobileDetect from '../hooks/useMobileDetect';

// Calendar Components
import {
  CalendarHeader,
  CalendarContextMenu,
  CalendarSlotTooltip,
  MultipleBookingsModal,
  AppointmentModal,
  EditAppointmentModal,
  AvailabilityModal,
  DeleteAvailabilityModal,
  CancelConfirmationModal,
  ReleaseSlotModal,
  MonthView,
  WeekView,
  DayView
} from '../components/calendar';
import MobileWeekView from '../components/calendar/views/MobileWeekView';

import { getWeekStart, formatDate } from '../utils/calendarHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const CalendarView = () => {
  const { calendarId } = useParams();
  const { token, isAdmin, getAuthHeaders, isCustomer, isCalendarAdmin, runningUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();  // BOOKING DATE NAVIGATION (Jan 15, 2026)
  
  // BOOKING DATE FROM URL (Jan 15, 2026):
  // If customer comes from confirmation email, navigate directly to booking date
  const bookingDateParam = searchParams.get('date');
  
  // MOBILE DETECTION (Jan 15, 2026):
  // Automatically detect mobile devices and adjust calendar layout
  const { isMobile, isTablet, isTouchDevice } = useMobileDetect();
  
  // MOBILE HEADER COLLAPSE STATE (Jan 15, 2026):
  // Header defaults to collapsed on mobile for cleaner UI
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(true);
  
  // Calendar Data State
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [slotOccupancy, setSlotOccupancy] = useState({ booked_slots: [], held_slots: [] }); // All bookings for availability calc
  const [availability, setAvailability] = useState([]);
  const [monthAvailability, setMonthAvailability] = useState({});
  const [services, setServices] = useState([]);
  const [servers, setServers] = useState([]);
  
  // Calendar Sync Modal State - REMOVED (Jan 15, 2026)
  // Calendar sync moved to Profile Settings page
  
  // View State
  const [viewMode, setViewMode] = useState('week');
  // Initialize dates - if booking date param exists, use that date
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    if (bookingDateParam) {
      const bookingDate = new Date(bookingDateParam + 'T00:00:00');
      return getWeekStart(bookingDate);
    }
    return getWeekStart(new Date());
  });
  const [currentDate, setCurrentDate] = useState(() => {
    if (bookingDateParam) {
      return new Date(bookingDateParam + 'T00:00:00');
    }
    return new Date();
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // UI State
  const [contextMenu, setContextMenu] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [dragState, setDragState] = useState(null);
  
  // Selection State
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  
  // Modal State
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showDeleteAvailabilityModal, setShowDeleteAvailabilityModal] = useState(false);
  const [showReleaseSlotModal, setShowReleaseSlotModal] = useState(false);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);
  const [showMultipleBookingsModal, setShowMultipleBookingsModal] = useState(false);
  
  // Message Console State (for right-click on booking)
  const [showMessageConsole, setShowMessageConsole] = useState(false);
  const [messageConsoleCustomer, setMessageConsoleCustomer] = useState(null);

  // Week days array
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(currentWeekStart);
    date.setDate(currentWeekStart.getDate() + i);
    return date;
  });

  // Office hours and time slots
  const getOfficeHours = () => {
    if (!calendar?.office_hours) return { start: 9, end: 17 };
    const match = calendar.office_hours.match(/(\d+).*?(\d+)/);
    if (match) {
      let start = parseInt(match[1]);
      let end = parseInt(match[2]);
      if (calendar.office_hours.toLowerCase().includes('pm') && end < 12) {
        end += 12;
      }
      return { start, end };
    }
    return { start: 9, end: 17 };
  };

  const officeHours = getOfficeHours();
  const timeSlots = Array.from(
    { length: officeHours.end - officeHours.start },
    (_, i) => officeHours.start + i
  );

  // Effects
  useEffect(() => {
    if (!token) {
      navigate('/find-calendar');
      return;
    }
    fetchCalendar();
  }, [calendarId]);

  useEffect(() => {
    if (calendar) {
      fetchAppointmentsAndAvailability();
    }
  }, [currentWeekStart, calendar]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (viewMode === 'month' && calendar) {
      fetchMonthAvailability();
    }
  }, [viewMode, currentDate, calendar]);

  // Drag handling
  useEffect(() => {
    if (dragState) {
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      
      const handleMouseMove = (e) => {
        if (!dragState) return;
        e.preventDefault();
      };
      
      const handleMouseUp = async (e) => {
        if (!dragState) return;
        
        const deltaY = e.clientY - dragState.startY;
        const pixelsPerHour = 80;
        const deltaHours = deltaY / pixelsPerHour;
        const delta15Min = Math.round(deltaHours * 4) / 4;
        
        const [origHour, origMin] = (dragState.edge === 'top' 
          ? dragState.originalStartTime 
          : dragState.originalEndTime).split(':').map(Number);
        
        const origDecimal = origHour + (origMin / 60);
        const newDecimal = Math.max(0, Math.min(24, origDecimal + delta15Min));
        const newHour = Math.floor(newDecimal);
        const newMin = Math.round((newDecimal - newHour) * 60);
        const newTime = `${newHour.toString().padStart(2, '0')}:${newMin.toString().padStart(2, '0')}`;
        
        let newStartTime = dragState.originalStartTime;
        let newEndTime = dragState.originalEndTime;
        
        if (dragState.edge === 'top') {
          newStartTime = newTime;
          if (newStartTime >= dragState.originalEndTime) {
            setDragState(null);
            return;
          }
        } else {
          newEndTime = newTime;
          if (newEndTime <= dragState.originalStartTime) {
            setDragState(null);
            return;
          }
        }
        
        try {
          await axios.put(
            `${API_BASE_URL}/availability/${dragState.availId}`,
            { start_time: newStartTime, end_time: newEndTime },
            { headers: getAuthHeaders() }
          );
          await fetchAppointmentsAndAvailability();
        } catch (err) {
          console.error('Error updating availability:', err);
          alert('Failed to update availability: ' + (err.response?.data?.detail || err.message));
        }
        
        setDragState(null);
      };
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState]);

  // API Functions
  const fetchCalendar = async () => {
    try {
      setLoading(true);
      // Use isCustomer() which checks runningUser - simple and consistent
      const isCustomerUser = isCustomer();
      let calendarData, assignedServices;
      
      // For customers, use the public endpoint (handles symbolic name lookup)
      // For admins, use authenticated endpoint
      if (isCustomerUser) {
        try {
          const response = await axios.get(`${API_BASE_URL}/public/calendars/${calendarId}`);
          calendarData = response.data.data.calendar;
          assignedServices = response.data.data.services || [];
        } catch (publicErr) {
          throw publicErr;
        }
      } else {
        // Admin or Calendar Admin - use authenticated endpoint
        const adminHeaders = getAuthHeaders();
        
        const response = await axios.get(`${API_BASE_URL}/calendars/${calendarId}`, {
          headers: adminHeaders
        });
        calendarData = response.data.data;
        
        const servicesRes = await axios.get(`${API_BASE_URL}/calendar-services/${calendarId}/services`, {
          headers: adminHeaders
        });
        assignedServices = servicesRes.data.data.assigned || [];
      }
      
      setCalendar(calendarData);
      setServices(assignedServices);
      
      if (calendarData.multiple_servers && !isCustomerUser) {
        try {
          const serversRes = await axios.get(`${API_BASE_URL}/calendar-servers/${calendarId}/servers`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setServers(serversRes.data.data.assigned || []);
        } catch (err) {
          console.error('Error fetching servers:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching calendar:', err);
      setError('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointmentsAndAvailability = async () => {
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const startDate = formatDate(currentWeekStart);
      const endDate = formatDate(weekEnd);
      const actualCalendarId = calendar?.id || calendarId;

      // Fetch user's own appointments (for display)
      const appointmentsRes = await axios.get(`${API_BASE_URL}/appointments`, {
        params: { calendar_id: actualCalendarId, start_date: startDate, end_date: endDate },
        headers: getAuthHeaders()
      });
      setAppointments(appointmentsRes.data.data || []);

      // SLOT AVAILABILITY FIX (Jan 2026):
      // For customers, also fetch slot occupancy data to show which slots are already booked
      // This includes ALL bookings on the calendar (without exposing customer details)
      // so customers can see accurate availability when booking
      if (isCustomer()) {
        try {
          const slotAvailRes = await axios.get(`${API_BASE_URL}/appointments/slot-availability/${actualCalendarId}`, {
            params: { start_date: startDate, end_date: endDate },
            headers: getAuthHeaders()
          });
          setSlotOccupancy(slotAvailRes.data.data || { booked_slots: [], held_slots: [] });
        } catch (err) {
          console.error('Error fetching slot availability:', err);
          setSlotOccupancy({ booked_slots: [], held_slots: [] });
        }
      }

      const availabilityRes = await axios.get(`${API_BASE_URL}/availability`, {
        params: { calendar_id: actualCalendarId, start_date: startDate, end_date: endDate },
        headers: getAuthHeaders()
      });
      setAvailability(availabilityRes.data.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const fetchMonthAvailability = async () => {
    if (!calendar) return;
    
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const actualCalendarId = calendar?.id || calendarId;
      
      const availabilityRes = await axios.get(`${API_BASE_URL}/availability`, {
        params: { 
          calendar_id: actualCalendarId, 
          start_date: formatDate(firstDay), 
          end_date: formatDate(lastDay) 
        },
        headers: getAuthHeaders()
      });
      
      const appointmentsRes = await axios.get(`${API_BASE_URL}/appointments`, {
        params: { 
          calendar_id: actualCalendarId, 
          start_date: formatDate(firstDay), 
          end_date: formatDate(lastDay) 
        },
        headers: getAuthHeaders()
      });
      
      const availData = availabilityRes.data.data || [];
      const apptData = appointmentsRes.data.data || [];
      const statusByDate = {};
      
      availData.forEach(avail => {
        if (!statusByDate[avail.date]) {
          statusByDate[avail.date] = { hasAvailability: false, hasAppointments: false };
        }
        statusByDate[avail.date].hasAvailability = true;
      });
      
      apptData.forEach(appt => {
        if (!statusByDate[appt.date]) {
          statusByDate[appt.date] = { hasAvailability: false, hasAppointments: false };
        }
        statusByDate[appt.date].hasAppointments = true;
      });
      
      setMonthAvailability(statusByDate);
    } catch (err) {
      console.error('Error fetching month availability:', err);
    }
  };

  // Handler for opening message console from booking right-click
  const handleOpenMessageConsole = async (appointment) => {
    if (!appointment) return;
    
    try {
      // Fetch full customer data to get email
      const response = await axios.get(`${API_BASE_URL}/admin/users/${appointment.customer_id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.data) {
        setMessageConsoleCustomer(response.data.data);
        setShowMessageConsole(true);
      } else {
        // Fallback to appointment data if fetch fails
        const customer = {
          id: appointment.customer_id,
          full_name: appointment.customer_name,
          email: ''
        };
        setMessageConsoleCustomer(customer);
        setShowMessageConsole(true);
      }
    } catch (err) {
      console.error('Error fetching customer:', err);
      // Fallback to appointment data
      const customer = {
        id: appointment.customer_id,
        full_name: appointment.customer_name,
        email: ''
      };
      setMessageConsoleCustomer(customer);
      setShowMessageConsole(true);
    }
  };

  // Action Handlers
  const handleToggleHoldStatus = async () => {
    if (!selectedAvailability || !selectedSlot) return;
    
    if (selectedAvailability.is_on_hold) {
      setShowReleaseSlotModal(true);
      return;
    }
    
    try {
      await axios.put(
        `${API_BASE_URL}/availability/${selectedAvailability.id}/toggle-hour-hold`,
        { date: selectedSlot.date, hour: selectedSlot.hour, is_on_hold: true },
        { headers: getAuthHeaders() }
      );
      await fetchAppointmentsAndAvailability();
    } catch (err) {
      console.error('Error holding slot:', err);
      alert('Failed to hold slot. Please try again.');
    }
  };

  const handleReleaseSlotConfirm = async (notificationData) => {
    if (!selectedAvailability || !selectedSlot) return;
    
    try {
      await axios.put(
        `${API_BASE_URL}/availability/${selectedAvailability.id}/toggle-hour-hold`,
        { date: selectedSlot.date, hour: selectedSlot.hour, is_on_hold: false },
        { headers: getAuthHeaders() }
      );
      
      if (notificationData.send) {
        await axios.post(
          `${API_BASE_URL}/availability/notify-release`,
          {
            availability_id: selectedAvailability.id,
            calendar_id: calendarId,
            notification_method: notificationData.method,
            recipient_type: notificationData.recipientType,
            selected_customers: notificationData.selectedCustomers,
            custom_message: notificationData.message
          },
          { headers: getAuthHeaders() }
        );
      }
      
      setShowReleaseSlotModal(false);
      await fetchAppointmentsAndAvailability();
    } catch (err) {
      console.error('Error releasing slot:', err);
      alert('Failed to release slot. Please try again.');
    }
  };

  // Loading/Error States
  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (error || !calendar) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-red-600 text-lg mb-4">{error || 'Calendar not found'}</p>
            <button
              onClick={() => navigate('/admin/calendars')}
              className="text-yellow-500 hover:text-yellow-600"
            >
              Back to Calendars
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-8">
        <div className="max-w-[1600px] mx-auto px-6">
          {/* Back button - only show for admins, not for customers */}
          {!isCustomer() && (
            <button
              onClick={() => navigate('/admin/calendars')}
              className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Manage Calendars
            </button>
          )}

          {/* Calendar Header with Navigation 
              MOBILE COLLAPSIBLE HEADER (Jan 15, 2026):
              - On mobile: Header is collapsible, defaults to collapsed for cleaner UI
              - Shows calendar name and expand button when collapsed
              - Desktop: Always shows full header
          */}
          {isMobile && isCustomer() ? (
            <div className="mb-4">
              {/* Collapsed Header Bar */}
              <div 
                className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between cursor-pointer"
                onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
              >
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-yellow-600" />
                  <span className="font-semibold text-gray-800">{calendar?.name || 'Calendar'}</span>
                </div>
                <button 
                  className="text-gray-500 hover:text-gray-700 p-1"
                  aria-label={isHeaderCollapsed ? 'Show calendar details' : 'Hide calendar details'}
                >
                  <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isHeaderCollapsed ? '' : 'rotate-180'}`} />
                </button>
              </div>
              
              {/* Expandable Header Content */}
              {!isHeaderCollapsed && (
                <div className="mt-2 animate-in slide-in-from-top-2 duration-200">
                  <CalendarHeader
                    calendar={calendar}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    currentWeekStart={currentWeekStart}
                    setCurrentWeekStart={setCurrentWeekStart}
                  />
                </div>
              )}
            </div>
          ) : (
            <CalendarHeader
              calendar={calendar}
              viewMode={viewMode}
              setViewMode={setViewMode}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              currentWeekStart={currentWeekStart}
              setCurrentWeekStart={setCurrentWeekStart}
            />
          )}

          {/* Calendar Views */}
          {viewMode === 'month' && (
            <MonthView
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              setCurrentWeekStart={setCurrentWeekStart}
              setViewMode={setViewMode}
              monthAvailability={monthAvailability}
              availability={availability}
              setSelectedSlot={setSelectedSlot}
              setSelectedAvailability={setSelectedAvailability}
              setContextMenu={setContextMenu}
            />
          )}

          {viewMode === 'day' && (
            <DayView
              currentDate={currentDate}
              timeSlots={timeSlots}
              appointments={appointments}
              slotOccupancy={slotOccupancy}
              availability={availability}
              calendar={calendar}
              services={services}
              servers={servers}
              user={runningUser}
              isAdmin={isAdmin}
              isCustomer={isCustomer}
              setSelectedSlot={setSelectedSlot}
              setSelectedAvailability={setSelectedAvailability}
              setSelectedAppointment={setSelectedAppointment}
              setContextMenu={setContextMenu}
              setShowEditAppointmentModal={setShowEditAppointmentModal}
              setHoveredSlot={setHoveredSlot}
              setTooltipPosition={setTooltipPosition}
            />
          )}

          {viewMode === 'week' && (
            /* MOBILE RESPONSIVE CALENDAR (Jan 15, 2026):
               - Mobile devices (< 768px): Use MobileWeekView with single-day navigation
               - Tablet/Desktop: Use standard WeekView with full week grid
               - For customers on mobile: Optimized touch interactions and larger targets
            */
            isMobile && isCustomer() ? (
              <MobileWeekView
                weekDays={weekDays}
                timeSlots={timeSlots}
                appointments={appointments}
                slotOccupancy={slotOccupancy}
                availability={availability}
                calendar={calendar}
                services={services}
                servers={servers}
                user={runningUser}
                isAdmin={isAdmin}
                isCustomer={isCustomer}
                setSelectedSlot={setSelectedSlot}
                setSelectedAvailability={setSelectedAvailability}
                setSelectedAppointment={setSelectedAppointment}
                setContextMenu={setContextMenu}
                setShowAppointmentModal={setShowAppointmentModal}
                setShowEditAppointmentModal={setShowEditAppointmentModal}
                token={token}
                getAuthHeaders={getAuthHeaders}
                initialDate={bookingDateParam}  // BOOKING DATE NAVIGATION: Pass date to set initial day
              />
            ) : (
              <WeekView
                weekDays={weekDays}
                timeSlots={timeSlots}
                appointments={appointments}
                slotOccupancy={slotOccupancy}
                availability={availability}
                calendar={calendar}
                services={services}
                servers={servers}
                user={runningUser}
                isAdmin={isAdmin}
                isCustomer={isCustomer}
                currentTime={currentTime}
                dragState={dragState}
                setDragState={setDragState}
                setSelectedSlot={setSelectedSlot}
                setSelectedAvailability={setSelectedAvailability}
                setSelectedAppointment={setSelectedAppointment}
                setContextMenu={setContextMenu}
                setShowAppointmentModal={setShowAppointmentModal}
                setShowEditAppointmentModal={setShowEditAppointmentModal}
                setShowMultipleBookingsModal={setShowMultipleBookingsModal}
                setHoveredSlot={setHoveredSlot}
                setTooltipPosition={setTooltipPosition}
                setCurrentDate={setCurrentDate}
                setCurrentWeekStart={setCurrentWeekStart}
                setViewMode={setViewMode}
                getWeekStart={getWeekStart}
                getAuthHeaders={getAuthHeaders}
              />
            )
          )}
        </div>
      </section>
      <Footer />

      {/* Tooltip */}
      <CalendarSlotTooltip
        hoveredSlot={hoveredSlot}
        tooltipPosition={tooltipPosition}
        services={services}
        servers={servers}
        isAdmin={isAdmin()}
        calendar={calendar}
        user={runningUser}
      />

      {/* Context Menu */}
      <CalendarContextMenu
        contextMenu={contextMenu}
        isAdmin={isAdmin()}
        selectedAvailability={selectedAvailability}
        onToggleHoldStatus={handleToggleHoldStatus}
        onDeleteAvailability={() => setShowDeleteAvailabilityModal(true)}
        onAppointmentClick={() => setShowAppointmentModal(true)}
        onAvailabilityClick={() => setShowAvailabilityModal(true)}
        onEditAppointment={() => setShowEditAppointmentModal(true)}
        onCancelAppointment={() => {
          // CUSTOMER CANCEL (Jan 15, 2026):
          // When customer clicks Cancel, show cancel confirmation modal
          if (selectedAppointment) {
            setAppointmentToCancel(selectedAppointment);
            setShowCancelConfirmationModal(true);
          }
        }}
        onSendMessage={handleOpenMessageConsole}
        onClose={() => setContextMenu(null)}
      />

      {/* Modals */}
      {showAppointmentModal && (
        <AppointmentModal
          calendarId={calendar?.id || calendarId}
          calendar={calendar}
          selectedSlot={selectedSlot}
          services={services}
          onClose={() => {
            setShowAppointmentModal(false);
            setSelectedSlot(null);
            fetchAppointmentsAndAvailability();
          }}
          getAuthHeaders={getAuthHeaders}
          isCustomer={isCustomer()}
        />
      )}

      {showEditAppointmentModal && (
        <EditAppointmentModal
          calendarId={calendar?.id || calendarId}
          calendar={calendar}
          appointment={selectedAppointment}
          services={services}
          onClose={() => {
            setShowEditAppointmentModal(false);
            setSelectedAppointment(null);
            fetchAppointmentsAndAvailability();
          }}
          getAuthHeaders={getAuthHeaders}
          isCustomer={isCustomer()}
          onCancelClick={(apt) => {
            setAppointmentToCancel(apt);
            setShowCancelConfirmationModal(true);
          }}
        />
      )}

      {isAdmin() && showAvailabilityModal && (
        <AvailabilityModal
          calendarId={calendarId}
          calendar={calendar}
          selectedSlot={selectedSlot}
          onClose={() => {
            setShowAvailabilityModal(false);
            setSelectedSlot(null);
            fetchAppointmentsAndAvailability();
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {isAdmin() && showDeleteAvailabilityModal && (
        <DeleteAvailabilityModal
          availability={selectedAvailability}
          selectedSlot={selectedSlot}
          calendar={calendar}
          onClose={() => {
            setShowDeleteAvailabilityModal(false);
            setSelectedAvailability(null);
            setSelectedSlot(null);
            fetchAppointmentsAndAvailability();
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {isAdmin() && showReleaseSlotModal && (
        <ReleaseSlotModal
          slot={selectedAvailability}
          selectedHour={selectedSlot}
          calendarId={calendarId}
          onClose={() => setShowReleaseSlotModal(false)}
          onConfirm={handleReleaseSlotConfirm}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {showCancelConfirmationModal && appointmentToCancel && (
        <CancelConfirmationModal
          appointment={appointmentToCancel}
          services={services}
          onConfirm={() => {
            setShowCancelConfirmationModal(false);
            setAppointmentToCancel(null);
            fetchAppointmentsAndAvailability();
          }}
          onClose={() => {
            setShowCancelConfirmationModal(false);
            setAppointmentToCancel(null);
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Multiple Bookings Modal */}
      {showMultipleBookingsModal && selectedSlot?.appointments && (
        <MultipleBookingsModal
          selectedSlot={selectedSlot}
          services={services}
          servers={servers}
          onClose={() => setShowMultipleBookingsModal(false)}
          onEditAppointment={(apt) => {
            setSelectedAppointment(apt);
            setShowMultipleBookingsModal(false);
            setShowEditAppointmentModal(true);
          }}
          onCancelAppointment={(apt) => {
            setAppointmentToCancel(apt);
            setShowMultipleBookingsModal(false);
            setShowCancelConfirmationModal(true);
          }}
        />
      )}
      
      {/* Message Console for sending messages to booking customers */}
      {showMessageConsole && messageConsoleCustomer && (
        <MessageConsole
          isOpen={showMessageConsole}
          onClose={() => {
            setShowMessageConsole(false);
            setMessageConsoleCustomer(null);
          }}
          customer={messageConsoleCustomer}
        />
      )}
      
      {/* Calendar Sync Modal - REMOVED (Jan 15, 2026)
          Moved to Profile Settings page for better organization */}
      
      {/* Floating Chat Button for Customers - Regular messages only */}
      <CustomerChatButton />
      
      {/* Support Chat Button - listens for support chat events from Header */}
      <SupportChatButton />
    </div>
  );
};

export default CalendarView;
